<template>
  <div class="node-map" :class="[`node-map--${arranjo}`]">
    <div class="node-map__campo" :style="{ '--lado': `${lado}px`, '--vaos': Math.max(1, nos.length - 1) }">
      <!--
        A TRILHA. É desenhada em SVG e não com `div`s porque uma trilha que ENCHE precisa de
        `stroke-dasharray`, e não existe equivalente honesto disso em CSS de caixa.

        `viewBox` de 0 a 100 nos dois eixos: as posições dos nós são percentuais, então a mesma
        geometria serve à roda de 320px do telefone e à de 480px do desktop sem recalcular nada.
      -->
      <svg class="node-map__trilha" viewBox="0 0 100 100" aria-hidden="true" preserveAspectRatio="none">
        <path v-for="(d, i) in caminhos" :key="`base-${i}`" :d="d" class="node-map__trilha-base" />
        <!-- `pathLength=100` normaliza o comprimento: o dasharray/dashoffset viram percentuais
             exatos do caminho, e o trecho cheio parte SEMPRE do primeiro nó. A versão anterior
             chutava um comprimento maior que o real e o enchimento aparecia na ponta errada da
             rota (visto em screenshot na carta náutica). -->
        <template v-if="progresso > 0">
          <path
            v-for="(d, i) in caminhos"
            :key="`cheia-${i}`"
            :d="d"
            pathLength="100"
            class="node-map__trilha-cheia"
            :style="{ strokeDasharray: 100, strokeDashoffset: 100 * (1 - progresso) }"
          />
        </template>
      </svg>

      <!--
        Os nós recebem `role="tablist"`/`tab` de propósito: para um leitor de tela, "um conjunto de
        opções em que uma está escolhida e o painel ao lado mostra a escolhida" é literalmente o
        padrão de abas. Inventar um papel novo aqui daria menos, não mais.
      -->
      <div
        class="node-map__nos"
        role="tablist"
        :aria-label="rotulo"
        aria-orientation="horizontal"
        @keydown.left.prevent="andar(-1)"
        @keydown.up.prevent="andar(-1)"
        @keydown.right.prevent="andar(1)"
        @keydown.down.prevent="andar(1)"
        @keydown.home.prevent="ir(0)"
        @keydown.end.prevent="ir(nos.length - 1)"
      >
        <button
          v-for="(no, i) in nos"
          :key="no.id"
          :ref="`no-${i}`"
          class="node-map__no"
          :class="{
            'node-map__no--ativo': no.id === modelValue,
            'node-map__no--aceso': no.aceso,
            'node-map__no--bruma': no.bruma,
            'node-map__no--aqui': no.aqui,
            'node-map__no--pleno': no.pleno,
          }"
          type="button"
          role="tab"
          :aria-selected="no.id === modelValue ? 'true' : 'false'"
          :tabindex="no.id === modelValue ? 0 : -1"
          :style="posicaoDoNo(i)"
          @click="ir(i)"
        >
          <FishIcon v-if="no.icone && !no.bruma" :nome="no.icone" tamanho="pequeno" />
          <span v-if="no.bruma" class="node-map__bruma" aria-hidden="true" />
          <span v-else-if="no.grau != null" class="node-map__grau t-numero">{{ no.grau }}</span>

          <!-- O anúncio completo do nó (7.2b): nome, nível e custo, numa frase. -->
          <span class="so-leitor">{{ anunciar(no) }}</span>

          <span v-if="no.estrelas != null && !no.bruma" class="node-map__estrelas" aria-hidden="true">
            <i v-for="e in 3" :key="e" :class="['node-map__estrela', { 'node-map__estrela--cheia': e <= no.estrelas }]" />
          </span>
        </button>

        <!-- O barco marca onde você está (D21). Só no arranjo linear. -->
        <div v-if="arranjo === 'linear' && posicaoDoBarco" class="node-map__barco repouso-flutua" :style="posicaoDoBarco" aria-hidden="true">
          <FishIcon nome="ship" tamanho="pequeno" />
        </div>
      </div>

      <!-- O centro da roda é a RESTRIÇÃO COMUM: o ouro (D19). No linear ele não existe. -->
      <div v-if="arranjo === 'ciclico' && $slots.centro" class="node-map__centro">
        <slot name="centro" />
      </div>
    </div>

    <!-- O painel de detalhe: ao lado no desktop, embaixo no telefone (D19, D21). Quem não passa
         o slot não ganha a coluna vazia — é o caso do trilho, em que um painel só serve as três
         árvores. -->
    <div v-if="$slots.detalhe" class="node-map__detalhe">
      <slot name="detalhe" :no="noAtivo" />
    </div>
  </div>
</template>

<script>
import FishIcon from './FishIcon.vue';
import { caminhosDaTrilha, posicoesDosNos } from './nodeMapGeometria';

/**
 * 2.10b — `FishNodeMap`: nós, trilha e painel de detalhe.
 *
 * ## Por que a roda e a carta são o MESMO componente (D21)
 *
 * > A roda de melhorias e a carta de mapas são **a mesma coisa em duas formas**: nós, trilha e
 * > painel de detalhe. A roda é **cíclica** porque os atributos não têm ordem; a carta é **linear**
 * > porque os mapas têm. Então é **um primitivo só**, com dois arranjos. Escrever duas vezes seria
 * > repetir exatamente o erro que gerou as 407 cores.
 *
 * ## O que cada arranjo entrega
 *
 * **Cíclico (D19, 7.2)** — oito atributos num octógono. Oito cabem **exatamente**: espaçamento par,
 * simétrico, sem sobra. O nó mostra **três coisas e só três**: ícone, anel e o número do nível.
 * Nada de descrição, preço ou botão — foi justamente isso que matou a tela atual, em que oito cards
 * repetiam rótulo, efeito, acordeão de custos, preço e botão.
 *
 * **Linear (D21, 7.5)** — sete paradas numa rota, com o barco na posição atual e **bruma** nas de
 * além. E a bruma tem **dois usos** que precisam parecer diferentes: `bloqueado` (falta nível,
 * barco ou ouro) mostra o requisito; `por vir` (`Costa Dourada`, `Ilhas Cazu` — a atualização das
 * ilhas, D22-nota) mostra **só a bruma, sem requisito escrito**. O horizonte que promete, não a
 * porta trancada.
 *
 * ## Os dois detalhes que fazem a roda valer a pena (D19)
 *
 * **1. O anel do nó diz se dá para comprar** (`aceso`). Aceso se o ouro cobre o próximo nível. O
 * jogador vê *o que dá para fazer agora* sem abrir nada — é o D6 aplicado.
 *
 * **2. A trilha entre os nós enche** conforme o progresso total sobe. É o que dá à roda uma razão
 * para ser roda em vez de enfeite: ela mostra o progresso geral, não oito botões em círculo.
 *
 * ## O que a roda piora, e a compensação (D19, 7.2b)
 *
 * Sendo honesto: **layout radial é pior que lista para comparar.** Quem quer saber qual melhoria é
 * mais barata precisa de oito toques onde uma tabela mostraria tudo.
 *
 * A compensação que este componente implementa é a terceira do D19: **teclado e leitor de tela
 * recebem ordem linear.** As setas andam pelo anel na ordem em que os nós aparecem, e cada nó
 * anuncia nome, nível e custo (`anunciar()`). Para quem usa leitor de tela, a roda **já é** a lista
 * que o D19 diz que faltaria.
 *
 * ## Trilho (1.3, D12)
 *
 * O terceiro arranjo: uma árvore de habilidades como **dois trilhos paralelos que se juntam**,
 * correndo da esquerda para a direita. Cada nó diz a `coluna` (o passo) e a `fileira` (0 em cima,
 * 1 embaixo, 0.5 no meio); os dois trilhos percorrem as suas fileiras e convergem no primeiro nó
 * do meio, que é a junção. Dali em diante é um só, até o capstone.
 *
 * **Por que trilho, e não camadas.** A primeira versão empilhava as habilidades em cinco fileiras
 * com uma espinha vertical, uma árvore por vez atrás de sub-abas — e o jogador não via o que as
 * outras duas ofereciam, que é justamente a decisão que a tela pede (só um capstone entre as
 * três). Três trilhos empilhados mostram as 36 de uma vez.
 *
 * **Ele não encolhe.** Sete colunas em 360px dariam nós de 30px, ilegíveis. O campo tem largura
 * mínima e quem rola é o container da tela; girar o trilho no telefone seria uma segunda
 * geometria para manter.
 */
export default {
  name: 'FishNodeMap',
  components: { FishIcon },
  props: {
    /**
     * `[{ id, rotulo, icone?, grau?, aceso?, bruma?, aqui?, pleno?, estrelas?, custo?, nota? }]`
     *
     * `grau` é o número no nó: o nível do atributo (D19) ou o número da parada (D21). `pleno`
     * marca o nó que não recebe mais nada — o que está no nível máximo (1.3).
     *
     * No arranjo `trilho` o nó traz também `coluna` (o passo, a partir de 1) e `fileira` (0 em
     * cima, 1 embaixo, 0.5 no meio — depois da junção).
     */
    nos: { type: Array, required: true },
    modelValue: { type: [String, Number], default: null },
    arranjo: {
      type: String,
      default: 'ciclico',
      validator: (v) => ['ciclico', 'linear', 'trilho'].includes(v),
    },
    /** 0 a 1 — quanto da trilha está cheia. */
    progresso: { type: Number, default: 0 },
    rotulo: { type: String, default: 'Nós' },
    /** O lado do campo em px. 320 cabe num telefone de 360 com margem (D19). */
    lado: { type: Number, default: 320 },
  },
  emits: ['update:modelValue'],
  computed: {
    noAtivo() {
      return this.nos.find((n) => n.id === this.modelValue) || null;
    },
    /**
     * A geometria vive em `nodeMapGeometria.js`, e não aqui, por um motivo prático: o jest deste
     * cliente não transforma SFC, então o que fica dentro do componente não tem teste — e
     * geometria errada não lança erro, só fica torta na tela. Lá ela é entrada e saída, e tem.
     */
    posicoes() {
      return posicoesDosNos(this.arranjo, this.nos);
    },
    caminhos() {
      return caminhosDaTrilha(this.arranjo, this.nos, this.posicoes);
    },
    posicaoDoBarco() {
      const i = this.nos.findIndex((n) => n.aqui);
      if (i < 0) return null;
      const { x, y } = this.posicoes[i];
      return { left: `${x}%`, top: `${y}%` };
    },
  },
  methods: {
    posicaoDoNo(i) {
      const { x, y } = this.posicoes[i];
      return { left: `${x}%`, top: `${y}%` };
    },
    ir(i) {
      const no = this.nos[i];
      if (!no) return;
      this.$emit('update:modelValue', no.id);
      this.$nextTick(() => {
        const alvo = this.$refs[`no-${i}`];
        (Array.isArray(alvo) ? alvo[0] : alvo)?.focus?.();
      });
    },
    /**
     * A ordem linear do teclado (7.2b).
     *
     * No **cíclico** ela dá a volta: da última seta direita para a primeira, porque o anel é
     * circular e parar na última seria mentir sobre a forma.
     *
     * No **linear** ela para nas pontas: a rota tem começo e fim, e voltar do último ao primeiro
     * sugeriria que o Pier vem depois das Ilhas Cazu.
     */
    andar(passo) {
      const atual = this.nos.findIndex((n) => n.id === this.modelValue);
      const base = atual < 0 ? 0 : atual;

      if (this.arranjo === 'ciclico') {
        this.ir((base + passo + this.nos.length) % this.nos.length);
        return;
      }
      this.ir(Math.max(0, Math.min(this.nos.length - 1, base + passo)));
    },
    /** Nome, nível e custo, numa frase (D19). É o que o nó "mostra" para quem não vê o anel. */
    anunciar(no) {
      const partes = [no.rotulo];
      if (no.grau != null) partes.push(`nível ${no.grau}`);
      if (no.custo != null) partes.push(`custo ${Number(no.custo).toLocaleString('pt-BR')}`);
      if (no.estrelas != null) partes.push(`${no.estrelas} de 3 estrelas`);
      if (no.bruma) partes.push('sob bruma');
      else if (no.aceso) partes.push('disponível');
      else if (no.custo != null) partes.push('ouro insuficiente');
      if (no.aqui) partes.push('você está aqui');
      return partes.join(', ');
    },
  },
};
</script>

<style scoped>
.node-map {
  display: flex;
  flex-direction: column;
  gap: var(--e-4);
}

/**
 * O painel NUNCA é mais largo que a linha.
 *
 * `min-width: 0` aqui fora, e não dentro da media query: sem ele o painel é dimensionado pelo
 * **max-content** do que tem dentro — na carta, a tira de peixes de dez silhuetas — e isso aparece
 * de dois jeitos opostos, os dois errados (vistos em screenshot). Com muitos peixes o detalhe passa
 * da moldura de papel e o botão e a lista saem para cima da água; com poucos ele ENCOLHE abaixo da
 * largura do papel e a legenda fica numa faixa estreita, desalinhada da rota.
 */
.node-map__detalhe {
  min-width: 0;
}

/**
 * Lado a lado só na RODA (D19) — e é por isso que o seletor não é `.node-map`.
 *
 * A roda é um quadrado de 320 a 480px: no desktop sobra largura ao lado dela e o painel cabe. A
 * rota (D21) e o trilho (1.3) são LARGOS e baixos — já ocupam a linha inteira, e o painel deles
 * mora embaixo, como legenda. Os dois vinham desligando esta regra por fora (o trilho numa media
 * query própria, a carta num `:deep`), e a carta desligava só a DIREÇÃO: o `align-items:
 * flex-start` continuava valendo numa coluna, tirava o `stretch` do painel e era o que o mandava
 * para o max-content.
 */
@media (min-width: 900px) {
  .node-map--ciclico {
    flex-direction: row;
    align-items: flex-start;
  }

  .node-map--ciclico .node-map__detalhe {
    flex: 1;
  }
}

.node-map__campo {
  position: relative;
  width: var(--lado);
  height: var(--lado);
  max-width: 100%;
  flex-shrink: 0;
  margin: 0 auto;
}

/* O TRILHO (1.3): largo e baixo, duas fileiras. Ele NÃO encolhe — sete colunas em 360px dariam
 * nós de 30px. A largura mínima é o que faz o container da tela rolar na horizontal. */
.node-map--trilho .node-map__campo {
  width: 100%;
  min-width: 600px;
  max-width: none;
  height: 180px;
  margin: 0;
}

.node-map--trilho .node-map__no {
  width: 60px;
  height: 60px;
}

/**
 * O linear é largo e baixo: uma rota não é um quadrado.
 *
 * **A parada encolhe com a ROTA, não com a janela.** `--passo` é a distância entre duas paradas
 * (os 88% que a geometria usa, divididos pelos vãos) e a parada nunca passa de 80% dele — em
 * qualquer largura, sempre sobra vão entre uma e a seguinte. O piso de 40px é o alvo de toque
 * (D11), o teto de 76px é o tamanho da moeda no desktop.
 *
 * Era uma media query de 599px, e media query mede a JANELA: a mesma carta abre dentro do modal de
 * Pescar (7.5c), onde a janela é larga e a rota é estreita, e ali as paradas continuavam de 76px
 * num campo que não as comportava. Percentual mede o campo, que é o que importa.
 */
.node-map--linear .node-map__campo {
  --passo: calc(88% / var(--vaos, 1));
  --no-lado: clamp(40px, calc(var(--passo) * 0.8), 76px);
  width: 100%;
  height: calc(var(--lado) * 0.72);
}

/* `aspect-ratio`, e não `height: var(--no-lado)`: `--no-lado` guarda uma porcentagem, e
 * porcentagem em `height` mede a ALTURA do campo — a moeda saía elipse, larga e baixa. */
.node-map--linear .node-map__no {
  width: var(--no-lado);
  height: auto;
  aspect-ratio: 1;
}

/* As duas paradas das PONTAS ficam a 6% da borda e são centradas no ponto: metade delas caía fora
 * do campo, por cima da moldura de papel da carta. Recuar a área de desenho meia parada de cada
 * lado resolve sem mexer na geometria — e recua a trilha junto, senão a rota deixaria de passar
 * pelas paradas. */
.node-map--linear .node-map__trilha,
.node-map--linear .node-map__nos {
  left: calc(var(--no-lado) / 2);
  /* Largura explícita, e não `right` com `width: auto`: o `<svg>` é um elemento SUBSTITUÍDO, e num
   * absoluto substituído a largura automática vem do tamanho intrínseco — o `right` é ignorado e a
   * trilha ficava mais larga que os nós, saindo do campo pela direita. */
  right: auto;
  width: calc(100% - var(--no-lado));
}

/* Em tela estreita a rota fica mais ALTA: a serpentina precisa de curso vertical para separar as
 * paradas que a largura de 360px aproxima. */
@media (max-width: 599px) {
  .node-map--linear .node-map__campo {
    height: calc(var(--lado) * 0.95);
  }
}

.node-map__trilha {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.node-map__trilha-base,
.node-map__trilha-cheia {
  fill: none;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  /* `non-scaling-stroke` mantém a espessura em pixels mesmo com o viewBox esticado pelo
   * `preserveAspectRatio="none"` do arranjo linear — sem isso a rota afina de um lado. */
  vector-effect: non-scaling-stroke;
}

.node-map__trilha-base {
  stroke: var(--madeira-sombra);
  /* A rota é PONTILHADA (D21: "sete paradas ligadas por uma rota em linha pontilhada"). */
  stroke-dasharray: 3 3;
}

.node-map__trilha-cheia {
  stroke: var(--ouro);
  transition: stroke-dashoffset var(--t-cena) var(--curva-entrada);
}

.node-map__nos {
  position: absolute;
  inset: 0;
}

.node-map__no {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;

  width: 76px; /* D19 — oito nós de 56px numa circunferência de ~1005px sobram folgados */
  height: 76px;
  min-width: 0;
  min-height: 0;
  padding: 0;

  /* O no e uma MOEDA DE LATAO sobre a agua: madeira escura com aro. Sobre pergaminho ele
   * precisa ser escuro, senao some — foi o que a inversao de superficie exigiu rever. */
  border: 3px solid var(--madeira-escura);
  border-radius: var(--r-circulo);
  background: var(--madeira);
  color: var(--papel);

  transition: transform var(--t-rapido) var(--curva-quique),
    border-color var(--t-rapido) var(--curva-suave), box-shadow var(--t-rapido) var(--curva-suave);
}

/**
 * O ANEL ACESO — o detalhe nº 1 do D19.
 *
 * Aceso = o ouro cobre o próximo nível. É o que responde "o que dá para fazer agora" sem abrir
 * nada. Repare que ele **não pulsa**: um anel piscando em oito nós seria a tela inteira nervosa, e
 * o D0 veta. Ele só fica aceso.
 */
.node-map__no--aceso {
  border-color: var(--ouro);
  background: var(--madeira-clara);
  color: var(--papel-luz);
  box-shadow: var(--brilho-suave);
}

.node-map__no--ativo {
  border-color: var(--ouro-claro);
  background: var(--ouro);
  color: var(--ouro-escuro);
  transform: translate(-50%, -50%) scale(1.12);
}

@media (hover: hover) {
  .node-map__no:hover {
    transform: translate(-50%, -50%) scale(1.08);
  }
  .node-map__no--ativo:hover {
    transform: translate(-50%, -50%) scale(1.12);
  }
}

/* A bruma (D21): a parada some atrás dela. Sem requisito escrito quando é "por vir". */
.node-map__no--bruma {
  border-color: var(--cor-neutra);
  background: var(--madeira-sombra);
  opacity: 0.55;
}

.node-map__bruma {
  width: 20px;
  height: 20px;
  border-radius: var(--r-circulo);
  background: var(--cor-neutra);
  opacity: 0.5;
  filter: blur(3px);
}

.node-map__no--aqui {
  border-color: var(--agua-espuma);
}

/* O nó no MÁXIMO (1.3). Verde do papel "bom", e sem o brilho do aceso: ele não pede clique, ele
 * diz que terminou. Vem depois do `--aceso` de propósito — um nó cheio nunca está aceso. */
.node-map__no--pleno {
  border-color: var(--cor-bom);
  background: var(--madeira-clara);
  box-shadow: none;
}

.node-map__grau {
  font-size: var(--f-2);
}

.node-map__centro {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 42%;
  text-align: center;
}

.node-map__barco {
  position: absolute;
  transform: translate(-50%, -140%);
  pointer-events: none;
}

/* As estrelas por parada (D21): quantos peixes daquele mapa você descobriu. Três é o mapa
 * completo — e é o que amarra a carta à enciclopédia (D14). */
.node-map__estrelas {
  display: flex;
  gap: 1px;
}

.node-map__estrela {
  width: 5px;
  height: 5px;
  background: var(--papel-sombra);
  clip-path: polygon(50% 0, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
}

.node-map__estrela--cheia {
  background: var(--ouro-claro);
}
</style>
